
import { api } from '../core';
import { IntegrationFlow } from '../../types';
import { MOCK_FLOWS } from '../mockData';
import { localDB } from '../helpers';

// Helper to map API Response (FlowOutputVM) to UI Model (IntegrationFlow)
const mapToUI = (apiFlow: any): IntegrationFlow => {
    return {
        id: apiFlow.id,
        name: apiFlow.name,
        active: apiFlow.isActive,
        // The API stores nodes/edges as stringified JSON
        nodes: apiFlow.nodes ? JSON.parse(apiFlow.nodes) : [],
        edges: apiFlow.edges ? JSON.parse(apiFlow.edges) : [],
        lastExecuted: apiFlow.lastExecutedAt ? new Date(apiFlow.lastExecutedAt).getTime() : undefined,
        endpointUrl: apiFlow.webhookUrl
    };
};

// Helper to map UI Model to API Request (FlowCreateVM)
const mapToAPI = (flow: Partial<IntegrationFlow>) => {
    return {
        name: flow.name,
        description: `Flow created on ${new Date().toLocaleDateString()}`,
        nodes: flow.nodes ? JSON.stringify(flow.nodes) : "[]",
        edges: flow.edges ? JSON.stringify(flow.edges) : "[]",
        isActive: flow.active,
        isPublic: false
    };
};

export const FlowsService = {
  getAll: async (): Promise<IntegrationFlow[]> => {
    return api.execute<IntegrationFlow[]>(
      '/api/V1/Flow/GetAllByLanguage',
      async () => localDB.get<IntegrationFlow[]>('lahja_flows', MOCK_FLOWS),
      { mockDelay: 500 }
    ).then(res => {
        // If it's a real API call, map the results. If it's mock (local), it's already IntegrationFlow[]
        if (Array.isArray(res) && res.length > 0 && 'isActive' in res[0]) {
            return res.map(mapToUI);
        }
        return res as IntegrationFlow[];
    });
  },

  save: async (flow: IntegrationFlow): Promise<IntegrationFlow> => {
    return api.execute<IntegrationFlow>(
      `/api/V1/Flow/Update/${flow.id}`,
      async () => {
        localDB.updateItem('lahja_flows', flow, []);
        return flow;
      },
      // API expects { id: "...", body: { ...FlowCreateVM... } }
      { 
          method: 'PUT', 
          body: { id: flow.id, body: mapToAPI(flow) }
      }
    ).then(res => ('isActive' in (res as any) ? mapToUI(res) : res));
  },

  create: async (flow: Omit<IntegrationFlow, 'id'>): Promise<IntegrationFlow> => {
    const newFlowLocal = { ...flow, id: Date.now().toString() } as IntegrationFlow;
    
    return api.execute<IntegrationFlow>(
      '/api/V1/Flow/Create',
      async () => {
        localDB.updateItem('lahja_flows', newFlowLocal, []);
        return newFlowLocal;
      },
      { 
          method: 'POST', 
          body: mapToAPI(flow)
      }
    ).then(res => ('isActive' in (res as any) ? mapToUI(res) : res));
  },

  delete: async (id: string): Promise<boolean> => {
    return api.execute<boolean>(
      `/api/V1/Flow/Delete/${id}`,
      async () => {
        localDB.deleteItem('lahja_flows', id, []);
        return true;
      },
      { method: 'DELETE' }
    );
  }
};
